import { useState, useMemo, useEffect } from 'react';
import {
  useQueryParam,
  StringParam,
  JsonParam,
  NumberParam,
} from 'use-query-params';
import { Input } from 'antd';
import styled from 'styled-components';
import Filter, { IFilterProps } from './Filter';
import FilterGroup from './FilterGroup';
import type { IFilterGroupProps } from './FilterGroup';
import { useNormalizeFilters } from './normalizeFilters';
import ModelInstanceFilter from '../../../components/ModelInstanceFilter';
import transformKeyToJsonLogic, {
  createComparison,
  wrapArraySegments,
} from '../../utils/transformKeysToJsonLogic';
import type { Value as FilterValue } from '../../../components/ModelInstanceFilter';
import { buildQueryName } from '../../../utils';
import { buildSearchJsonLogic } from '../../utils/filterHelpers';

export type IFilterOrGroupProps = IFilterProps | IFilterGroupProps;

interface ISearchProps {
  enabled: boolean;
  searchableFields: string[];
  placeholder?: string;
}

export interface IProps {
  prefix?: string;
  filters: { [key: string]: IFilterOrGroupProps };
  search: ISearchProps;
  style?: React.CSSProperties;
  context?: Record<string, any>;
  onChange?: (jsonLogic: object | null) => void;
}

const FiltersSection: React.FC<IProps> = ({
  prefix,
  filters,
  search,
  style,
  onChange,
  context,
}) => {
  const [triggerChange, setTriggerChange] = useState<boolean>(false);

  const normalizedFilters = useNormalizeFilters(filters);

  // Current shown filters on the UI
  const [activeFilters, setActiveFilters] = useState<string[]>(() => {
    return normalizedFilters
      .filter(f => f.defaultVisible && f.visible !== false)
      .map(f => f.key);
  });

  // Filter values from the URL
  const [filterValues, setFilterValues] = useQueryParam(
    buildQueryName('filters', prefix),
    JsonParam
  );
  const filterValuesWithDefaultValue = filterValues || {};

  // Search value from the URL
  const [searchValue, setSearchValue] = useQueryParam(
    buildQueryName('search', prefix),
    StringParam
  );
  const searchWithDefaultValue = searchValue || '';

  // Search value from the URL
  const [pageValue, setPageValue] = useQueryParam(
    buildQueryName('page', prefix),
    NumberParam
  );
  const pageWithDefaultValue = pageValue || 1;

  // Get list of filters that can be added (not currently active)
  const availableFilters = useMemo(() => {
    return normalizedFilters.filter(
      f => !activeFilters.includes(f.key) && f.visible !== false
    );
  }, [normalizedFilters, activeFilters]);

  // Get list of currently active filter configs
  const activeFilterConfigs = useMemo(() => {
    return normalizedFilters.filter(f => activeFilters.includes(f.key));
  }, [normalizedFilters, activeFilters]);

  // Every time triggerChange is true, build the combined JSON logic
  // and call the onChange callback with the combined JSON logic
  // in order to update the filters
  useEffect(() => {
    if (triggerChange) {
      const combinedJsonLogic = buildCombinedJsonLogic({
        searchValue: searchWithDefaultValue,
        searchableFields: search.searchableFields,
        filterValues: filterValuesWithDefaultValue,
        filters,
      });

      if (onChange) {
        onChange(combinedJsonLogic);
      }
      if (pageWithDefaultValue && pageWithDefaultValue !== 1) {
        setPageValue(1);
      }
    }

    return () => {
      setTriggerChange(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerChange]);

  // On mount, build the combined JSON logic and
  // call the onChange callback with the combined JSON logic if it exists
  // in order to set the initial filters
  useEffect(() => {
    const combinedJsonLogic = buildCombinedJsonLogic({
      searchValue: searchWithDefaultValue,
      searchableFields: search.searchableFields,
      filterValues: filterValuesWithDefaultValue,
      filters,
    });
    if (onChange) {
      onChange(combinedJsonLogic);
    }
  }, []);

  // Handle adding a filter to the UI
  const handleAddFilter = (filterKey: string) => {
    setActiveFilters(prev => [...prev, filterKey]);
  };

  // Handle removing a filter from the UI, remove the filter from the filterValues
  // and call the onChange callback with the combined JSON logic
  // in order to update the filters
  const handleRemoveFilter = (filterKey: string) => {
    setActiveFilters(prev => prev.filter(f => f !== filterKey));

    const next = { ...filterValues } as any;
    delete next[filterKey];
    setFilterValues(next);

    handleTriggerChange();
  };

  // Handle adding more filters to the UI
  const handleAddMoreFilters = (filterKeys: string[]) => {
    const diff = filterKeys.filter(f => !activeFilters.includes(f));
    diff.forEach(f => {
      handleAddFilter(f);
    });
  };

  // Handle applying a filter to the UI
  // and call the onChange callback with the combined JSON logic
  // in order to update the filters
  const handleFilterApply = (filterKey: string, selected: string[]) => {
    setFilterValues({
      ...filterValues,
      [filterKey]: selected,
    });
    handleTriggerChange();
  };

  // Handle applying a filter group's per-child selections to the UI
  // and call the onChange callback with the combined JSON logic
  // in order to update the filters
  const handleGroupApply = (
    groupKey: string,
    selections: { [childKey: string]: string[] }
  ) => {
    setFilterValues({
      ...filterValues,
      [groupKey]: selections,
    });
    handleTriggerChange();
  };

  // Handle triggering a change in the filters
  // by setting triggerChange to true
  const handleTriggerChange = () => {
    setTimeout(() => {
      setTriggerChange(true);
    }, 100);
  };

  return (
    <FiltersContainer style={style}>
      <FiltersRow>
        {search.enabled && (
          <SearchWrapper>
            <Input.Search
              placeholder={search.placeholder || 'Search...'}
              onSearch={() => setTriggerChange(true)}
              enterButton
              value={searchWithDefaultValue}
              onChange={e => setSearchValue(e.target.value)}
              allowClear
              onClear={() => {
                setTimeout(() => {
                  setTriggerChange(true);
                }, 100);
              }}
            />
          </SearchWrapper>
        )}

        <FiltersWrapper>
          {activeFilterConfigs.map(filterConfig => {
            if (filterConfig.type === 'group') {
              return (
                <FilterWrapper key={filterConfig.key}>
                  <FilterGroup
                    {...filterConfig}
                    default={
                      filterValuesWithDefaultValue[filterConfig.key] || {}
                    }
                    onApply={selections =>
                      handleGroupApply(filterConfig.key, selections)
                    }
                    onRemove={
                      !filterConfig.defaultVisible
                        ? () => handleRemoveFilter(filterConfig.key)
                        : undefined
                    }
                    context={context}
                  />
                </FilterWrapper>
              );
            }

            return (
              <FilterWrapper key={filterConfig.key}>
                <Filter
                  {...filterConfig}
                  default={filterValuesWithDefaultValue[filterConfig.key] || []}
                  name={filterConfig.name}
                  filter={filterConfig.filter}
                  options={filterConfig.options}
                  modelInstanceOptions={filterConfig.modelInstanceOptions}
                  onApply={selected =>
                    handleFilterApply(filterConfig.key, selected)
                  }
                  onRemove={
                    !filterConfig.defaultVisible
                      ? () => handleRemoveFilter(filterConfig.key)
                      : undefined
                  }
                  context={context}
                />
              </FilterWrapper>
            );
          })}

          {availableFilters.length > 0 && (
            <ModelInstanceFilter
              searchable={true}
              selected={[]}
              name="More Filters"
              filters={availableFilters.map(f => ({
                label: f.name,
                value: f.key,
              }))}
              onApply={handleAddMoreFilters}
            />
          )}
        </FiltersWrapper>
      </FiltersRow>
    </FiltersContainer>
  );
};

export default FiltersSection;

const FiltersContainer = styled.div`
  width: 100%;
`;

const FiltersRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  flex-wrap: wrap;
`;

const SearchWrapper = styled.div`
  width: 250px;
`;

const FiltersWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  flex: 1;
`;

const FilterWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const buildCombinedJsonLogic = ({
  searchValue,
  searchableFields,
  filterValues,
  filters,
}: {
  searchValue: string;
  searchableFields: string[];
  filterValues: {
    [key: string]: FilterValue[] | { [childKey: string]: FilterValue[] };
  };
  filters: { [key: string]: IFilterOrGroupProps };
}) => {
  const logicParts: object[] = [];

  // Add search logic first if it exists
  if (searchValue) {
    logicParts.push(buildSearchJsonLogic(searchableFields, searchValue));
  }

  // Add all filter/group logic
  Object.keys(filterValues).forEach(filterKey => {
    const config = filters[filterKey];
    if (!config) {
      return;
    }

    if (config.type === 'group') {
      const groupSelections = filterValues[filterKey] as {
        [childKey: string]: string[];
      };
      const groupLogic = buildGroupJsonLogic({
        basePath: config.basePath,
        childFilters: config.filters,
        selections: groupSelections || {},
      });
      if (groupLogic) {
        logicParts.push(groupLogic);
      }
      return;
    }

    const values = filterValues[filterKey] as FilterValue[];
    if (values && values.length > 0) {
      logicParts.push(
        transformKeyToJsonLogic(config.filter, values as any, false)
      );
    }
  });

  // If no filters, return null
  if (logicParts.length === 0) {
    return null;
  }

  // If only one filter, return it directly
  if (logicParts.length === 1) {
    return logicParts[0];
  }

  // Multiple filters: combine with AND
  return {
    and: logicParts,
  };
};

/**
 * Combine a FilterGroup's child filters into one correlated jsonLogic block, so
 * all conditions apply to the SAME array element instead of independent "some" blocks.
 * Example: basePath "sudCases[].variations[]" with children on "type.id" and "class.id" =>
 * {"some": [{"var":"sudCases"}, {"some": [{"var":"variations"}, {"and": [
 *   {"in": [{"var":"type.id"}, [...]]},
 *   {"in": [{"var":"class.id"}, [...]]}
 * ]}]}]}
 *
 * Assumes each child's path, once basePath is stripped, has no further "[]" segments.
 */
const buildGroupJsonLogic = ({
  basePath,
  childFilters,
  selections,
}: {
  basePath: string;
  childFilters: { [key: string]: IFilterProps };
  selections: { [key: string]: string[] };
}): object | null => {
  const baseSegments = basePath
    .split('[]')
    .map(segment => (segment.startsWith('.') ? segment.slice(1) : segment))
    .filter(Boolean);

  const comparisons = Object.entries(childFilters)
    .map(([childKey, child]) => {
      const values = selections[childKey];
      if (!values || values.length === 0) {
        return undefined;
      }

      if (!child.filter.startsWith(basePath)) {
        throw new Error(
          `FilterGroup child "${childKey}" path "${child.filter}" must start with basePath "${basePath}"`
        );
      }

      const remainder = child.filter.slice(basePath.length).replace(/^\./, '');

      return createComparison(remainder, values as any, false);
    })
    .filter((comparison): comparison is object => Boolean(comparison));

  if (comparisons.length === 0) {
    return null;
  }

  const innermost =
    comparisons.length === 1 ? comparisons[0] : { and: comparisons };

  return wrapArraySegments(baseSegments, innermost);
};
